import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Languages, Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { useCreateWorkspace, useWorkspaces } from '@/lib/api';
import { CreateWorkspaceDialog } from '@/components/workspaces/CreateWorkspaceDialog';

export function WorkspacesPage() {
  const auth = useAuth();
  const toast = useToast();
  const workspaces = useWorkspaces();
  const createMutation = useCreateWorkspace({
    onError: (err) => toast.show(err.message, 'error'),
  });
  const [creating, setCreating] = useState(false);

  const onboarding = workspaces.isSuccess && workspaces.data.length === 0;
  const displayName =
    auth.user?.profile.given_name ??
    auth.user?.profile.preferred_username ??
    auth.user?.profile.email ??
    'there';

  return (
    <div className="space-y-6">
      {!onboarding && (
        <header className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Workspaces</h1>
            <p className="text-sm text-muted-foreground">
              Signed in as{' '}
              <span className="font-medium text-foreground">
                {auth.user?.profile.preferred_username ?? auth.user?.profile.email}
              </span>
            </p>
          </div>
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden />
            New workspace
          </Button>
        </header>
      )}

      {workspaces.isLoading && <Spinner label="Loading workspaces…" />}

      {workspaces.isError && (
        <p
          role="alert"
          className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
        >
          Failed to load workspaces: {workspaces.error.message}
        </p>
      )}

      {onboarding && (
        <section
          className="space-y-5 rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-transparent p-8"
          aria-labelledby="onboarding-heading"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Sparkles className="size-3" aria-hidden />
            Welcome to Fluentix
          </div>
          <div>
            <h1
              id="onboarding-heading"
              className="text-3xl font-semibold tracking-tight text-foreground"
            >
              Hi {displayName}, let's set up your first workspace.
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              A workspace holds your projects, source &amp; target languages, glossaries, and
              translators. You can have more than one — keep separate workspaces per product or
              per team if that suits you.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-sm font-medium text-foreground">Create a workspace</p>
              <p className="mt-1 text-xs text-muted-foreground">
                You become the workspace owner and can invite the rest of the team afterwards.
              </p>
              <Button className="mt-3" onClick={() => setCreating(true)}>
                <Plus aria-hidden />
                Create a workspace
              </Button>
            </div>
            <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4">
              <p className="text-sm font-medium text-foreground">Expecting an invite?</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Ask the workspace admin to add you as a member. Once they do, the workspace will
                appear here automatically — refresh the page to see it.
              </p>
            </div>
          </div>
        </section>
      )}

      {workspaces.isSuccess && workspaces.data.length > 0 && (
        <ul className="grid gap-3 sm:grid-cols-2">
          {workspaces.data.map((ws) => (
            <li key={ws.id}>
              <Link
                to={`/workspaces/${ws.id}`}
                className="block rounded-lg border bg-card p-4 text-card-foreground shadow-sm transition-colors hover:border-primary/40"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{ws.name}</span>
                  <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {ws.plan ?? 'free'}
                  </span>
                </div>
                <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Languages className="size-3" aria-hidden />
                  {ws.slug}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <CreateWorkspaceDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSubmit={async (input) => {
          const created = await createMutation.mutateAsync(input);
          toast.show(`Workspace “${created.name}” created`, 'success');
          setCreating(false);
        }}
        pending={createMutation.isPending}
      />
    </div>
  );
}
